import type { RoleEnum } from '@acme/api/client';
import type { Certificate } from 'crypto-pro';
import { useState } from 'react';
import type { AuthSecretResponse, AuthServiceInterface } from './services';

interface UseAuthOptions {
  authService: AuthServiceInterface;
  login: (tokenResponse: any) => void;
  onSuccessRedirect?: (userStatus?: any) => void;
  onErrorRedirect?: (error: string) => void;
}

export const useAuth = ({
  authService,
  login,
  onSuccessRedirect,
  onErrorRedirect,
}: UseAuthOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [
    remainingCountOfPasswordAttempts,
    setRemainingCountOfPasswordAttempts,
  ] = useState<number | null>(null);

  const auth = async (
    selectedCertificate: Certificate,
    secret: AuthSecretResponse,
    password?: string,
    role?: RoleEnum
  ) => {
    setLoading(true);
    setError(null);

    try {
      const resp = await authService.getAuthToken(
        selectedCertificate,
        secret.secret!,
        role,
        password
      );

      if (!resp.isSuccess) {
        const errorMessage =
          resp.errorText || 'Ошибка при выполнении запроса авторизации';
        setError(errorMessage);
        setRemainingCountOfPasswordAttempts(null);
        if (onErrorRedirect) {
          onErrorRedirect(errorMessage);
        }
        return;
      }

      if (!resp.data) {
        const errorMessage = resp.errorText || 'Сервер вернул пустой ответ';
        setError(errorMessage);
        setRemainingCountOfPasswordAttempts(null);
        if (onErrorRedirect) {
          onErrorRedirect(errorMessage);
        }
        return;
      }

      const tokenResponse = resp.data;

      if (!tokenResponse.isSuccess) {
        const remainingAttempts =
          tokenResponse.remainingCountOfPasswordAttempts ?? null;

        if (remainingAttempts === 0 || remainingAttempts === null) {
          const errorMessage = 'Превышен лимит ошибок';
          setError(errorMessage);
          setRemainingCountOfPasswordAttempts(null);
          if (onErrorRedirect) {
            onErrorRedirect(errorMessage);
          }
          return;
        }

        setError('Неверный пароль');
        setRemainingCountOfPasswordAttempts(remainingAttempts);
        return;
      }

      if (!tokenResponse.accessToken) {
        const errorMessage = resp.errorText || 'Токен доступа не был получен';
        setError(errorMessage);
        setRemainingCountOfPasswordAttempts(null);
        if (onErrorRedirect) {
          onErrorRedirect(errorMessage);
        }
        return;
      }

      setRemainingCountOfPasswordAttempts(null);
      login(tokenResponse);

      if (onSuccessRedirect) {
        onSuccessRedirect(tokenResponse.userStatus);
      }
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error ? e.message : 'Неожиданная ошибка при авторизации';
      setError(errorMessage);
      setRemainingCountOfPasswordAttempts(null);
      if (onErrorRedirect) {
        onErrorRedirect(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return { auth, loading, error, remainingCountOfPasswordAttempts };
};

interface UseRegistrationOptions {
  login: (tokenResponse: any) => void;
  onErrorRedirect?: (path: string) => void;
}
